import React from 'react'

import { Box, Card as MuiCard, CardContent, Typography, Grid, Tooltip as MuiTooltip } from 'components'

import { isUndefined } from 'lodash'
import { PieChart as RePieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'

import { ReactComponent as EmptySvg } from 'assets/svg/empty.svg'

interface IPieChart {
  label: string,
  data: {name: string, value: number}[],
  total?: number,
  totalType?: string
  totalTooltip?: string
  height?: number
  showPercentage?: boolean
}

const PieChart: React.FC<IPieChart> = ({ data, label, total, totalType, totalTooltip, height = 400, showPercentage = false }) => {
  const COLORS = ['#1abc9c', '#2980b9', '#8e44ad', '#e67e22', '#C2185B', '#009688', '#388E3C', '#00BCD4', '#5D4037', '#FF5722', '#F57C00', '#4CAF50', '#1976D2', '#ED4C67', '#B53471']

  const RenderTooltip = ({ title, children }: any) => {
    if (title) {
      return (
        <MuiTooltip title={title} arrow>
          {children}
        </MuiTooltip>
      )
    }
    return children
  }

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const RADIAN = Math.PI / 180
    const radius = innerRadius + (outerRadius - innerRadius) * 1.32
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    return (
      <text x={x} y={y} fill="black" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    )
  }

  // Custom tooltip formatter - always show quantitative value
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const value = payload[0].value

      return (
        <div style={{ backgroundColor: '#fff', padding: '5px 10px', border: '1px solid #ccc' }}>
          <p>{`${payload[0].name}: ${value}`}</p>
        </div>
      )
    }
    return null
  }

  // If there's no data, just show the empty SVG
  if (!data || !data.length) {
    return (
      <Box>
        <MuiCard>
          <CardContent>
            <Grid container justifyContent="center" alignItems="center">
              <Grid item xs={12}>
                <Typography component="h5" variant="h5">
                  {label}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center">
                  <EmptySvg width="270" height="270" />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </MuiCard>
      </Box>
    )
  }

  // Otherwise, render the chart
  return (
    <Box>
      <MuiCard>
        <CardContent>
          <Grid container justifyContent="center" alignItems="center">
            <Grid item xs={12}>
              <Typography component="h5" variant="h5">
                {label}
              </Typography>
            </Grid>
            {!isUndefined(total) && (
              <Grid item xs={4}>
                <RenderTooltip title={totalTooltip}>
                  <Box
                    display="flex"
                    justifyContent="center"
                    m="auto"
                    color={`${totalType}.contrastText`}
                    bgcolor={`${totalType}.main`}
                    p={2}
                    borderRadius={16}
                  >
                    <Typography component="h3" variant="h3">
                0
                    </Typography>
                  </Box>
                </RenderTooltip>
              </Grid>
            )}
            <Grid item xs={!isUndefined(total) ? 8 : 12}>
              <ResponsiveContainer height={height}>
                <RePieChart>
                  <Legend layout="horizontal" verticalAlign="top" align="left" />
                  <Pie
                    labelLine={showPercentage}
                    label={showPercentage ? renderCustomizedLabel : undefined}
                    data={data}
                    cx="50%"
                    cy="50%"
                    innerRadius={10}
                    outerRadius="50%"
                    fill="#8884d8"
                    dataKey="value"
                    paddingAngle={5}>
                    {data.map((_entry: any, index:any) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </RePieChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </CardContent>
      </MuiCard>
    </Box>)
}

export default PieChart
